import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
    Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import axios from '../../api/client';
import EmptyState from '../EmptyState';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { Box, Grid } from '@mui/material';
import ScrollTable from '../ScrollTable';
import TableToolbar from '../TableToolbar';
import useTableSearch from '../../hooks/useTableSearch';
import TenantPicker from '../TenantPicker';

const NoticeManagement = () => {
  const [notices, setNotices] = useState([]);

  // Client-side: this screen loads every row, so filtering here searches all of
  // them. Screens that page on the server search there instead.
  const {
    query, setQuery, filterValues, setFilter, reset,
    rows: visibleNotices, resultCount, totalCount
  } = useTableSearch(notices, {
    searchFields: ['title', 'message', 'tenant.name', 'property.propertyName'],
    filters: {
      status: (row, v) => row.status === v
    }
  });
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [formData, setFormData] = useState({
    status: '',
    comments: '',
  });
  const [createForm, setCreateForm] = useState({
    tenant: '',
    categoryId: '',
    noticeDate: new Date().toISOString().split('T')[0],
    effectiveDate: '',
    // A demand notice names two future dates that cannot be derived from anything: the day
    // the arrears must be cleared by, and the day eviction follows. Everything else on that
    // form comes from the tenant and their account.
    clearanceDeadline: '',
    evictionDate: '',
    reason: '',
    comments: '',
    status: 'pending',
  });

  useEffect(() => {
    fetchNotices();
    fetchCategories();
  }, []);

  // Only active categories — a retired one stays on the notices already issued under it
  // but is not offered for new ones. The server refuses it too.
  const fetchCategories = async () => {
    try {
      const response = await axios.get('/notice-categories?isActive=true');
      setCategories(response.data || []);
    } catch (error) {
      setCategories([]);
    }
  };

  // The chosen category decides whether an effective date is asked for and how the
  // notice will print.
  const selectedCategory = categories.find((c) => c._id === createForm.categoryId) || null;
  // Driven by the category's LAYOUT rather than its name, so renaming "Demand Notice" in the
  // admin panel does not quietly stop the extra fields appearing.
  const isDemand = selectedCategory?.documentTemplate === 'demand';
  const isEviction = selectedCategory?.documentTemplate === 'eviction';
  // Both served notices state the tenant's arrears, so both are filled from the account.
  const statesArrears = isDemand || isEviction;

  const fetchNotices = async () => {
    try {
      const response = await axios.get('/notices');
      setNotices(response.data);
    } catch (error) {
      toast.error('Error fetching notices');
    }
  };

  const handleReview = (notice) => {
    setSelectedNotice(notice._id);
    setFormData({
      status: notice.status,
      comments: notice.comments || '',
    });
    setOpen(true);
  };

  const handleSubmit = async () => {
    try {
      await axios.put(`/notices/${selectedNotice}`, formData);
      toast.success('Notice reviewed');
      setOpen(false);
      fetchNotices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error reviewing notice');
    }
  };

  const handleCreate = async () => {
    try {
      if (!createForm.tenant) {
        toast.error('Please select a tenant');
        return;
      }
      if (!createForm.categoryId) {
        toast.error('Please select the nature of this notice');
        return;
      }
      // Some categories must state when the notice takes effect — the formal layout
      // measures the notice period from it. The server enforces the same rule.
      if (isDemand) {
        if (!createForm.clearanceDeadline) {
          toast.error('Give the date the arrears must be cleared by');
          return;
        }
        if (!createForm.evictionDate) {
          toast.error('Give the date eviction would follow');
          return;
        }
        // The notice reads "clear by X, or eviction on Y". The other way round it demands
        // nothing — the deadline would have passed before it was served.
        if (createForm.evictionDate < createForm.clearanceDeadline) {
          toast.error('The eviction date cannot be before the date to clear by');
          return;
        }
      }

      if (selectedCategory?.requiresEffectiveDate && !createForm.effectiveDate) {
        toast.error(`A '${selectedCategory.name}' must state the date it takes effect`);
        return;
      }
      if (!createForm.noticeDate) {
        toast.error('Please select a notice date');
        return;
      }
      await axios.post('/notices', createForm);
      toast.success('Notice issued');
      setCreateOpen(false);
      setCreateForm({
        tenant: '',
        categoryId: '',
        clearanceDeadline: '',
        evictionDate: '',
        noticeDate: new Date().toISOString().split('T')[0],
        effectiveDate: '',
        reason: '',
        comments: '',
        status: 'pending',
      });
      fetchNotices();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error issuing notice');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
    };
    return colors[status] || 'default';
  };

  const handleViewPdf = async (noticeId) => {
    try {
      const response = await axios.get(`/notices/${noticeId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error viewing PDF');
    }
  };

  const handleDownloadPdf = async (noticeId) => {
    try {
      const response = await axios.get(`/notices/${noticeId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `notice-${noticeId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error downloading PDF');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Notice Management</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Issue Notice
        </Button>
      </Box>
      <TableToolbar
        search={query}
        onSearchChange={setQuery}
        searchPlaceholder="Title, message or tenant"
        onReset={reset}
        resultCount={resultCount}
        totalCount={totalCount}
        filters={[
          { key: 'status', label: 'Status', value: filterValues.status || '',
            onChange: (v) => setFilter('status', v),
            options: [...new Set(notices.map((n) => n.status))].filter(Boolean).map((x) => ({ value: x, label: x })) }
        ]}
      />
      <ScrollTable>
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Tenant</TableCell>
              <TableCell>Property</TableCell>
              <TableCell>Nature</TableCell>
              <TableCell>Notice Date</TableCell>
              <TableCell>Effective Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell className="pin-right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleNotices.map((notice) => (
              <TableRow key={notice._id}>
                <TableCell className="pin-left">
                  {notice.tenant?.user?.name || 'N/A'}
                </TableCell>
                <TableCell>
                  {notice.property?.propertyName || 'N/A'}
                </TableCell>
                {/* Notices issued before categories existed fall back to their old type. */}
                <TableCell>{notice.category?.name || notice.noticeType || '-'}</TableCell>
                <TableCell>
                  {format(new Date(notice.noticeDate), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell>
                  {notice.effectiveDate || notice.moveOutDate
                    ? format(new Date(notice.effectiveDate || notice.moveOutDate), 'MMM dd, yyyy')
                    : 'N/A'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={notice.status}
                    color={getStatusColor(notice.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell className="pin-right">
                  <Box display="flex" gap={1}>
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleReview(notice)}
                    >
                      Review
                    </Button>
                    {notice.pdfPath && (
                      <>
                        <Button
                          size="small"
                          startIcon={<PictureAsPdfIcon />}
                          onClick={() => handleViewPdf(notice._id)}
                        >
                          PDF
                        </Button>
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadPdf(notice._id)}
                        >
                          Download
                        </Button>
                      </>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {visibleNotices.length === 0 && (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState title="No notices yet" />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </ScrollTable>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Review Notice</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            select
            label="Status"
            margin="normal"
            value={formData.status}
            onChange={(e) =>
              setFormData({ ...formData, status: e.target.value })
            }
          >
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Comments"
            margin="normal"
            multiline
            rows={4}
            value={formData.comments}
            onChange={(e) =>
              setFormData({ ...formData, comments: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">
            Submit Review
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Notice Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Issue Notice</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TenantPicker
                valueId={createForm.tenant}
                required
                error={!createForm.tenant}
                helperText={!createForm.tenant ? 'Please select a tenant' : ''}
                onChange={(val) => setCreateForm({ ...createForm, tenant: val?._id || '' })}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                select
                required
                label="Nature of Notice"
                value={createForm.categoryId}
                onChange={(e) => setCreateForm({ ...createForm, categoryId: e.target.value })}
                helperText={
                  categories.length === 0
                    ? 'No categories set up — an admin can add them under Notice Categories'
                    : selectedCategory?.documentTemplate === 'formal'
                      ? 'Prints as a formal legal notice'
                      : selectedCategory
                        ? 'Prints as a standard notice'
                        : 'What kind of notice this is'
                }
              >
                {categories.map((category) => (
                  <MenuItem key={category._id} value={category._id}>{category.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Notice Date"
                InputLabelProps={{ shrink: true }}
                value={createForm.noticeDate}
                onChange={(e) => setCreateForm({ ...createForm, noticeDate: e.target.value })}
              />
            </Grid>
            {/* Only asked for when the category says so. A rent increment that applies from
                a stated month has no effective date to give; an evacuation notice does, and
                the formal layout measures the notice period from it. */}
            {selectedCategory?.requiresEffectiveDate && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Effective Date"
                  InputLabelProps={{ shrink: true }}
                  value={createForm.effectiveDate}
                  onChange={(e) => setCreateForm({ ...createForm, effectiveDate: e.target.value })}
                  helperText="When this notice takes effect"
                />
              </Grid>
            )}
            {/* Only for a demand. Every other blank on that form — the tenant, the
                premises, the amount owed and the months it covers — is filled from what the
                system already knows. These two are dates in the future that nobody could
                derive, so they are the only things worth asking for. */}
            {statesArrears && (
              <Grid item xs={12}>
                <Alert severity="info">
                  The tenant, premises, arrears and the months they cover are filled in
                  automatically from the account. The amount is recorded as it stands today,
                  so the notice keeps saying what it said when it was served.
                  {isEviction && ' Copies are addressed to the LC1, the landlord and the area police station.'}
                </Alert>
              </Grid>
            )}

            {isDemand && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth required type="date" label="Clear By"
                    InputLabelProps={{ shrink: true }}
                    value={createForm.clearanceDeadline}
                    onChange={(e) => setCreateForm({ ...createForm, clearanceDeadline: e.target.value })}
                    helperText="The date the arrears must reach zero"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth required type="date" label="Eviction Date"
                    InputLabelProps={{ shrink: true }}
                    value={createForm.evictionDate}
                    onChange={(e) => setCreateForm({ ...createForm, evictionDate: e.target.value })}
                    helperText="When eviction and confiscation would follow"
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                value={createForm.reason}
                onChange={(e) => setCreateForm({ ...createForm, reason: e.target.value })}
                placeholder="Reason for the notice..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Comments (optional)"
                multiline
                rows={3}
                value={createForm.comments}
                onChange={(e) => setCreateForm({ ...createForm, comments: e.target.value })}
                placeholder="Additional comments..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained">
            Issue Notice
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default NoticeManagement;

